import { useState } from 'react'
import { startCheckout } from '../lib/api'
import { languageFromHost, languageOf } from '../lib/languages'
import { useStore } from '../state/store'
import { PRICE_PER_MONTH } from '../views/SubscribeView'

/**
 * The clock, where the person on the trial can actually see it.
 *
 * Before this, a trial ran for seven days and the product said nothing about
 * it. The only prompt was a single email two days before expiry — so someone
 * who liked it on day two was never invited to pay, someone whose mail provider
 * filed that email under Promotions got no warning at all, and the first thing
 * either of them saw was a paywall on day eight. A subscribe button existed, in
 * Settings, which is not somewhere anybody goes to buy something.
 *
 * So: a slim strip, always present while a trial is running, saying how long is
 * left and offering to end the uncertainty in one click.
 *
 * Deliberately NOT dismissible. It is not an advertisement, it is the state of
 * the reader's account — and a dismissed banner puts us straight back to a
 * countdown nobody can see. It stays slim and calm instead, and earns its
 * urgency only in the last two days.
 */

const DAY_MS = 24 * 60 * 60 * 1000

/** Rounded up: half a day left is "1 day", never "0 days". */
export function trialDaysLeft(expiresAt: number, now = Date.now()): number {
  return Math.max(0, Math.ceil((expiresAt - now) / DAY_MS))
}

export function TrialBanner({
  standing,
}: {
  standing: { source: string; expiresAt: number | null } | null
}) {
  const { data } = useStore()
  const token = data.sync.token ?? ''
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const code = languageFromHost()

  // Only ever for a running trial. A paid subscription, a comp or an
  // administrator's grant have no clock to report, and inventing one would be a
  // false claim about somebody's money.
  if (!token || !code || standing?.source !== 'trial' || standing.expiresAt === null) return null

  const remaining = standing.expiresAt - Date.now()
  const days = trialDaysLeft(standing.expiresAt)
  /*
   * Under a day is "today", not "1 day left".
   *
   * The count rounds up so a part-day is never reported as none — but that same
   * rounding tells somebody with three hours left that they have a day, which
   * is the one moment it matters most to be accurate.
   */
  const endsToday = remaining < DAY_MS
  const lang = languageOf(code)
  // The last two days are when it stops being background information. That is
  // also when the reminder email goes out, so the two agree.
  const urgent = days <= 2

  async function subscribe() {
    setBusy(true)
    setError(null)
    const result = await startCheckout(token)
    if (!result.ok) {
      setBusy(false)
      setError(result.error)
      return
    }
    window.location.href = result.url
  }

  return (
    <div className={`trial-strip ${urgent ? 'is-urgent' : ''}`} role="status">
      <span className="trial-strip-text">
        {endsToday ? (
          <>Your free week of {lang.name} ends today.</>
        ) : (
          <>
            <strong>
              {days} {days === 1 ? 'day' : 'days'}
            </strong>{' '}
            left of your free week of {lang.name}.
          </>
        )}{' '}
        {/* Said plainly, because the fear it answers — "will I lose my work?" —
            is the actual reason people hesitate. */}
        <span className="trial-strip-calm">
          Nothing is charged automatically, and your islands are kept either way.
        </span>
      </span>

      {error ? (
        <span className="trial-strip-error">{error}</span>
      ) : (
        <button type="button" className="btn is-small is-primary" disabled={busy} onClick={() => void subscribe()}>
          {busy ? 'Opening…' : `Keep ${lang.name} — $${PRICE_PER_MONTH}/mo`}
        </button>
      )}
    </div>
  )
}
